'use client'

import ButtonOverlay from '@/components/embedding/ButtonOverlay'
import ProgressBar from '@/components/embedding/ProgressBar'
import VideoCanvas from '@/components/embedding/VideoCanvas'
import {
  ButtonActionType,
  type Hotspot,
  type InteractiveButton,
  type PlayerConfig,
  type PlayerState,
} from '@/types/interactiveVideo'
import { FC, MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BRANCH_CHOICE_LEAD_SEC } from './branchChoiceTiming'
import InteractiveVideoPlayerPresentation from './interactive-video-player-presentation'
import { useModalSurvey } from './modal/useModalSurvey'
import { withoutCompletedSurveyButtons } from './survey/completedSurveyButtons'
import { resolveSurveyTargetNodeKey, type RoutingAnswer } from './survey/surveyRouting'

export interface InteractiveVideoPlayerProps {
  config: PlayerConfig
  /** 回答保存 API 用のシナリオID。SURVEYノードのゲート送信に使う。プレビューでは未指定。 */
  scenarioId?: string
  onVideoChange?: (nodeId: string) => void
  onButtonClick?: (button: InteractiveButton) => void | Promise<void>
  /** 動画内のクリック領域が押された（計測用。動作の実行はプレイヤーが行う・ADR-0022） */
  onHotspotClick?: (hotspot: Hotspot) => void | Promise<void>
  onVideoEnd?: (nodeId: string) => void
  onClose?: () => void
  containerMode?: boolean
  /**
   * 親コンテナ内で描画しつつ、POPUP の角丸・影を維持する。
   * 管理画面プレビューが実際の script 表示を再現するために使う。
   */
  standaloneAppearance?: boolean
  /**
   * 「戻る」ボタンを出さない。BOX/WIDE は分岐ボタンだけで進む体験なので、
   * 履歴を巻き戻す導線を見せない（ADR-0016）。
   */
  hideBackButton?: boolean
  /**
   * プレイヤーの見た目。`wide` は横型16:9のインライン再生で、分岐は動画の最終フレームを
   * 残したまま暗転＋質問＋選択ボタンで見せる（ADR-0016）。
   */
  variant?: 'default' | 'wide'
  /**
   * ワイド型の並べ方。'stacked' はスマホ縦画面向けで、選択肢・アンケートを映像に重ねず動画の下に並べる。
   * 省略時（'overlay'）は従来どおり映像に重ねる。
   */
  wideLayout?: 'overlay' | 'stacked'
  /**
   * ワイド型の目次（ADR-0025）の置き方。'side' は動画の左に重ね、下の操作バーと一緒に出る・隠れる（PC の広い画面）、
   * 'overlay' は左上のボタンから動画の上に開く（狭い画面）。スマホ縦画面（stacked）は動画の下のボタンから開く。
   */
  wideAgendaPlacement?: 'side' | 'overlay'
  /**
   * 全画面化の対象要素。ワイド型のプレイヤーは自分の外側（16:9の枠ごと）を全画面にしたいので、
   * シェル側から渡す。未指定なら全画面ボタンは何もしない。
   */
  fullscreenTargetRef?: RefObject<HTMLElement | null>
  /**
   * 動画上の分岐ボタン（ButtonOverlay）を出さない。BOX の PC は選択肢を右パネルに
   * 置くため（デモA準拠・ADR-0016）、シェルが `playerApiRef` 経由で選択を流し込む。
   * テロップは出したままにする。
   */
  hideBranchButtons?: boolean
  /**
   * 動画紐づきアンケートを動画に重ねず、この要素へ portal して描画する。
   * BOX の PC（2カラム）が右パネルの枠を渡す。未指定なら従来どおり動画下部のシート。
   */
  surveyPortalElement?: HTMLElement | null
  /**
   * シェルからプレイヤーを操作するための窓口。BOX の右パネルに置いた選択肢から
   * ノード遷移・外部リンク（計測込み）を実行するのに使う。
   */
  playerApiRef?: MutableRefObject<InteractiveVideoPlayerApi | null>
  /**
   * 動画の object-fit。未指定は 'cover'。BOX の1カラムがタブレット幅で 'contain' を
   * 渡し、9:16 の上下が切れてテロップが消えるのを防ぐ。
   */
  videoFit?: 'cover' | 'contain'
  /**
   * 再生位置の通知。BOX が「残りN秒になったら選択肢を出す」判定に使う。
   * 毎秒数回呼ばれるので、受け手は state 更新を最小限に抑えること。
   */
  onTimeUpdate?: (currentTime: number, duration: number) => void
  /** アンケートの箱を答え終えた（BOX の右パネルが、そこへ進む選択肢を消すのに使う） */
  onSurveyGateComplete?: (nodeId: string) => void
}

export type InteractiveVideoPlayerApi = {
  /** ボタンIDで選択を実行する（計測 → 遷移/外部リンクまで通常のクリックと同じ経路） */
  selectButton: (buttonId: string) => void | Promise<void>
  /** 再生中なら一時停止する。シェル側のCVボタンで別タブへ送り出すときに使う */
  pauseVideo: () => void
  /** 今の動画を最初から再生し直す。バナー型の終了後の「もう一度見る」に使う（別の動画へは進まない） */
  replay: () => void
}

const InteractiveVideoPlayer: FC<InteractiveVideoPlayerProps> = ({
  config,
  scenarioId,
  onVideoChange,
  onButtonClick,
  onHotspotClick,
  onVideoEnd,
  onClose,
  containerMode,
  standaloneAppearance,
  hideBackButton,
  variant = 'default',
  wideLayout = 'overlay',
  wideAgendaPlacement = 'overlay',
  fullscreenTargetRef,
  hideBranchButtons,
  surveyPortalElement,
  playerApiRef,
  onTimeUpdate,
  videoFit,
  onSurveyGateComplete,
}) => {
  // Player state management
  const [state, setState] = useState<PlayerState>({
    currentNodeId: config.initialNodeId,
    history: [],
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: config.muted || false,
    playbackRate: 1.0,
    hasEnded: false,
  })

  const videoRef = useRef<HTMLVideoElement>(null)
  // 動画カードを離れたときの再生位置（ノードID→位置）。アンケートの箱を答え終えたら、来た元の動画へ
  // 戻って続きから再生するのに使う。ended は、動画が終わってから離れたか（戻ったら最後の画面を出す）。
  const resumePointsRef = useRef(new Map<string, { time: number; ended: boolean }>())
  // 戻った動画の読み込み後に一度だけ適用する再生位置
  const pendingResumeRef = useRef<{ nodeId: string; time: number; ended: boolean } | null>(null)

  // 同一視聴セッションで回答済みの設問 id 集合（SURVEYノードのゲートで再提示しないため・#263）。
  // ノードをまたいで保持し、リロードで消える（視聴セッション寿命＝コンポーネントのマウント寿命）。
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(() => new Set())
  // 答え終えたアンケートの箱（ノードキー）。そこへ進む選択肢ボタンを以降は出さない
  const [completedSurveyNodeIds, setCompletedSurveyNodeIds] = useState<Set<string>>(() => new Set())

  // Responsive: detect desktop/mobile
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768)
    }

    checkDesktop()
    window.addEventListener('resize', checkDesktop)

    return () => window.removeEventListener('resize', checkDesktop)
  }, [])

  // Get current node
  const currentNode = config.nodes.get(state.currentNodeId)

  // 動画紐づきアンケート（ADR-0014）: SLIDER/TILE と同一のシート提示機構（cue 同期・
  // 必須は強制ポーズ/視聴前ゲート・任意はオプトインタブ・回答保存）を動画ノードで使う。
  // SURVEYノード（全画面ゲート）の設問は SurveyGate が提示するため、ここには渡さない。
  const videoSurveyQuestions = useMemo(
    () => (currentNode?.type === 'SURVEY' ? [] : (currentNode?.surveyQuestions ?? [])),
    [currentNode],
  )
  // 別の動画へ進む選択肢があるノードは、末尾アンケートを終了の数秒前に出す（WIDE・POPUP）。
  // 選択肢は「必須の末尾アンケートに答えるまで隠す」ため、終了時提示のままだと
  // 「動画が終わった後のアンケートに答える → 選択肢が出る → 別の動画へ」の流れになる
  // （ADR-0023 A・branchChoiceTiming.ts）。BOX は選択肢を右パネル／下部バーに出して
  // アンケート中も隠さない（hideBranchButtons）ので、従来どおり終了時に出す。
  const hasVideoBranch = (currentNode?.buttons ?? []).some(
    (button) => button.type === ButtonActionType.VIDEO_TRANSITION,
  )
  const { survey: videoSurvey, pendingEndCount } = useModalSurvey({
    questions: videoSurveyQuestions,
    nodeKey: currentNode?.nodeKey ?? '',
    videoRef,
    target: scenarioId ? { kind: 'embed', scenarioId } : undefined,
    endCueLeadSec: hasVideoBranch && (variant === 'wide' || !hideBranchButtons) ? BRANCH_CHOICE_LEAD_SEC : undefined,
    active: true,
  })

  // CVボタン等で別タブへ送り出すときの一時停止。視聴者が戻ってきたときに
  // 動画が先へ進んでいる／音だけ鳴り続ける状態を防ぐ。
  // 再生ボタン（VideoCanvas）は state.isPlaying を見て再開するので、state も合わせる。
  const pauseVideo = useCallback(() => {
    const video = videoRef.current
    if (!video || video.paused) return
    video.pause()
    setState((prev) => ({ ...prev, isPlaying: false }))
  }, [])

  // 別の動画へ移る（分岐ボタン・クリック領域・目次で共通）。来た動画は履歴に積む
  const moveToNode = useCallback(
    (targetNodeId: string) => {
      setState((prev) => ({
        ...prev,
        history: [...prev.history, prev.currentNodeId],
        currentNodeId: targetNodeId,
        currentTime: 0,
        duration: 0,
        hasEnded: false,
      }))
      onVideoChange?.(targetNodeId)
    },
    [onVideoChange],
  )

  // Handle button selection
  const handleButtonSelect = useCallback(
    async (buttonId: string) => {
      if (!currentNode) return

      const button = currentNode.buttons.find((b) => b.id === buttonId)
      if (!button) return

      // Call button click callback and wait for tracking to complete
      await onButtonClick?.(button)

      // Handle different button types
      if (button.type === ButtonActionType.VIDEO_TRANSITION) {
        const action = button.action as { type: 'video_transition'; targetNodeId: string }

        // 動画カードから離れる位置を覚えておく（アンケートの箱のあとで続きから再生するため）
        if (currentNode.type !== 'SURVEY') {
          resumePointsRef.current.set(state.currentNodeId, {
            time: videoRef.current?.currentTime ?? state.currentTime,
            ended: state.hasEnded,
          })
        }

        moveToNode(action.targetNodeId)
      } else if (button.type === ButtonActionType.EXTERNAL_LINK) {
        const action = button.action as { type: 'external_link'; url: string; openInNewTab: boolean }

        // Open external link after tracking completes
        if (action.openInNewTab) {
          // 別タブへ移る間、この動画は裏で再生され続けてしまうので止める
          pauseVideo()
          window.open(action.url, '_blank')
        } else {
          window.location.href = action.url
        }
      }
    },
    [currentNode, onButtonClick, moveToNode, pauseVideo, state.currentNodeId, state.currentTime, state.hasEnded],
  )

  // クリック領域の「別の動画へ」で指定された、遷移先の再生開始位置（秒）。
  // <video> は同じ要素のまま src だけが差し替わるので、新しい動画の読み込み（loadedmetadata）を
  // 待ってからシークする。遷移先が同じ動画ファイルなら読み込みが起きないので、すぐシークする。
  const pendingStartRef = useRef<{ time: number; sameSource: boolean } | null>(null)

  useEffect(() => {
    const pending = pendingStartRef.current
    const video = videoRef.current
    if (!pending || !video) return

    const apply = () => {
      pendingStartRef.current = null
      const limit = Number.isFinite(video.duration) ? video.duration : pending.time
      video.currentTime = Math.min(pending.time, limit)
    }
    if (pending.sameSource) {
      apply()
      return
    }
    video.addEventListener('loadedmetadata', apply, { once: true })
    return () => video.removeEventListener('loadedmetadata', apply)
  }, [state.currentNodeId])

  // シーク（ワイド型のシークバー／目次の目印／終了画面の「もう一度見る」／クリック領域）。
  // 終了後にシークで戻ったらオーバーレイを畳み、再生も再開する
  // （終了画面から戻ったのに止まったまま、を避ける）。
  const handleSeek = useCallback((time: number) => {
    const video = videoRef.current
    if (!video) return

    const wasEnded = video.ended
    video.currentTime = time
    setState((prev) => ({ ...prev, currentTime: time, hasEnded: false }))
    if (wasEnded) {
      void video.play()
      setState((prev) => ({ ...prev, isPlaying: true }))
    }
  }, [])

  // 動画内のクリック領域（ADR-0022）。計測を待ってから動作を実行する（外部リンクで離脱する前に送るため）。
  const handleHotspotSelect = useCallback(
    async (hotspotId: string) => {
      const hotspot = currentNode?.hotspots?.find((item) => item.id === hotspotId)
      if (!currentNode || !hotspot) return

      await onHotspotClick?.(hotspot)

      const { action } = hotspot
      if (action.type === 'seek') {
        handleSeek(action.time)
        // 止めた映像の上で押された場合も、選んだシーンから再生を始める
        const video = videoRef.current
        if (video?.paused) {
          void video.play()
          setState((prev) => ({ ...prev, isPlaying: true }))
        }
        return
      }

      if (action.type === 'video_transition') {
        const target = config.nodes.get(action.targetNodeId)
        if (!target) return
        if (action.startTime > 0) {
          pendingStartRef.current = { time: action.startTime, sameSource: target.videoUrl === currentNode.videoUrl }
        }
        moveToNode(action.targetNodeId)
        return
      }

      if (action.openInNewTab) {
        // 別タブへ移る間、この動画は裏で再生され続けてしまうので止める
        pauseVideo()
        window.open(action.url, '_blank')
      } else {
        window.location.href = action.url
      }
    },
    [currentNode, config.nodes, onHotspotClick, moveToNode, pauseVideo, handleSeek],
  )

  // 必須アンケートの強制提示中は、目次から答えずに逃げられないよう押せなくする（ADR-0014）
  const isSurveyForced = Boolean(videoSurvey?.expanded && videoSurvey.isForced)

  // シェル（BOX の右パネル等）からボタン選択・一時停止・もう一度見るを流し込めるようにする
  useEffect(() => {
    if (!playerApiRef) return
    playerApiRef.current = { selectButton: handleButtonSelect, pauseVideo, replay: () => handleSeek(0) }
    return () => {
      playerApiRef.current = null
    }
  }, [playerApiRef, handleButtonSelect, pauseVideo, handleSeek])

  // SURVEYノードのゲートで設問に回答したら、視聴セッションの回答済み集合に加える（再提示防止）。
  const handleSurveyAnswered = useCallback((questionId: string) => {
    setAnsweredIds((prev) => {
      const next = new Set(prev)
      next.add(questionId)
      return next
    })
  }, [])

  // SURVEYノードのゲートを送信で通過したら、回答から遷移先を解決して進む
  // （ルーティング設問による分岐 / 未該当・線形はデフォルト遷移先・#265）。実在する遷移先だけ採用し、
  // 無ければ（未設定/存在しない/デフォルトも無い）フロー終端として安全に閉じる（#263）。
  const handleSurveyComplete = useCallback(
    (answers: RoutingAnswer[]) => {
      if (!currentNode) return
      const completedNodeId = state.currentNodeId
      setCompletedSurveyNodeIds((prev) => (prev.has(completedNodeId) ? prev : new Set(prev).add(completedNodeId)))
      onSurveyGateComplete?.(completedNodeId)
      // アンケートの箱のあとは動画へ進ませない（終点専用・ADR-0023 A-2）。以前に動画へつないだ設定が
      // 残っていても、行き先がアンケートの箱でなければフロー終端として閉じる。
      const target = resolveSurveyTargetNodeKey(
        currentNode,
        answers,
        (nodeKey) => config.nodes.get(nodeKey)?.type === 'SURVEY',
      )
      if (target) {
        setState((prev) => ({
          ...prev,
          history: [...prev.history, prev.currentNodeId],
          currentNodeId: target,
          currentTime: 0,
          duration: 0,
          hasEnded: false,
        }))
        onVideoChange?.(target)
        return
      }

      // アンケートを答え終えたら、来た元の動画へ戻って止めた位置から続きを再生する
      // （同じ動画に戻るだけで別の動画へは進まない・ADR-0023 A-2）。
      // 元の動画が無ければ（アンケートから始まった等）従来どおり閉じる。
      const history = state.history
      let originIndex = -1
      for (let i = history.length - 1; i >= 0; i--) {
        if (config.nodes.get(history[i])?.type !== 'SURVEY') {
          originIndex = i
          break
        }
      }
      if (originIndex < 0) {
        onClose?.()
        return
      }
      const originNodeId = history[originIndex]
      const resume = resumePointsRef.current.get(originNodeId) ?? { time: 0, ended: false }
      pendingResumeRef.current = { nodeId: originNodeId, ...resume }
      setState((prev) => ({
        ...prev,
        history: prev.history.slice(0, originIndex),
        currentNodeId: originNodeId,
        currentTime: resume.time,
        duration: 0,
        hasEnded: resume.ended,
      }))
      onVideoChange?.(originNodeId)
    },
    [currentNode, config.nodes, onVideoChange, onClose, state.history, state.currentNodeId, onSurveyGateComplete],
  )

  // 元の動画へ戻ったら、読み込み後に止めた位置へ移して再生を再開する
  // （動画が終わってから離れた場合は、最後の位置で止めたまま選択肢の画面を出す）。
  useEffect(() => {
    const pending = pendingResumeRef.current
    const video = videoRef.current
    if (!pending || pending.nodeId !== state.currentNodeId || !video) return

    const apply = () => {
      pendingResumeRef.current = null
      video.currentTime = pending.time
      if (pending.ended) {
        video.pause()
      } else {
        void video.play().catch(() => undefined)
      }
    }
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      apply()
      return
    }
    video.addEventListener('loadedmetadata', apply, { once: true })
    return () => video.removeEventListener('loadedmetadata', apply)
  }, [state.currentNodeId])

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (state.isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }))
    }
  }, [state.isPlaying])

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (state.history.length > 0) {
      const newHistory = [...state.history]
      const previousNodeId = newHistory.pop()!

      setState((prev) => ({
        ...prev,
        currentNodeId: previousNodeId,
        history: newHistory,
        currentTime: 0,
        duration: 0,
        hasEnded: false,
      }))

      onVideoChange?.(previousNodeId)
    }
  }, [state.history, onVideoChange])

  // Handle video end
  useEffect(() => {
    const handleVideoEnd = () => {
      // 最終フレームで静止したまま分岐・終了画面を重ねるため、終了を状態に残す（ADR-0016）
      setState((prev) => ({ ...prev, hasEnded: true, isPlaying: false }))
      onVideoEnd?.(state.currentNodeId)
    }

    const video = videoRef.current
    if (video) {
      video.addEventListener('ended', handleVideoEnd)
      return () => {
        video.removeEventListener('ended', handleVideoEnd)
      }
    }
  }, [state.currentNodeId, onVideoEnd])

  // 実際の再生・停止を state に反映する。自動再生（VideoCanvas）や終了後の再生など、
  // 再生ボタンを経由しない再生で state.isPlaying が false のまま残ると、再生ボタンが ▷ のままになり、
  // ワイド型の操作バーも「一時停止中」とみなして自動で隠れなくなる。
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const handlePlaying = () => setState((prev) => (prev.isPlaying ? prev : { ...prev, isPlaying: true }))
    const handlePause = () => setState((prev) => (prev.isPlaying ? { ...prev, isPlaying: false } : prev))
    video.addEventListener('play', handlePlaying)
    video.addEventListener('pause', handlePause)
    if (!video.paused) handlePlaying()
    return () => {
      video.removeEventListener('play', handlePlaying)
      video.removeEventListener('pause', handlePause)
    }
  }, [state.currentNodeId])

  // Handle time update
  const handleTimeUpdate = useCallback(
    (currentTime: number, duration: number) => {
      setState((prev) => ({ ...prev, currentTime, duration }))
      onTimeUpdate?.(currentTime, duration)
    },
    [onTimeUpdate],
  )

  // Handle mute toggle
  // 音量バーで0まで下げてからミュートを外したときは、押しても音が出ない状態にしないよう半分の音量に戻す
  const handleToggleMute = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMuted: !prev.isMuted,
      volume: prev.isMuted && prev.volume === 0 ? 0.5 : prev.volume,
    }))
  }, [])

  // 音量バー（ワイド型）。0まで下げたらミュート、ミュート中に上げたら音声オンにする
  const handleVolumeChange = useCallback((volume: number) => {
    setState((prev) => ({ ...prev, volume, isMuted: volume === 0 }))
  }, [])

  // 音あり自動再生（BOX/WIDE）がポリシーでブロックされ、VideoCanvas がミュートへ
  // 落として再生を続けたときの state 同期（ミュートボタンの表示を実態に合わせる）
  const handleAutoplayMuted = useCallback(() => {
    setState((prev) => ({ ...prev, isMuted: true }))
  }, [])

  // 全画面。iOS Safari は要素の全画面化に非対応なので、video 要素の webkit API に落とす。
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  const handleToggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
      return
    }

    const target = fullscreenTargetRef?.current
    if (target?.requestFullscreen) {
      void target.requestFullscreen()
      return
    }

    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null
    video?.webkitEnterFullscreen?.()
  }, [fullscreenTargetRef])

  // Handle playback rate change
  const handlePlaybackRateChange = useCallback((rate: number) => {
    setState((prev) => ({ ...prev, playbackRate: rate }))
    if (videoRef.current) {
      videoRef.current.playbackRate = rate
    }
  }, [])

  // 表示用の config。答え終えたアンケートの箱へ進む選択肢ボタンを除く（押下の解決は元の config で行う）
  const displayConfig = useMemo(() => {
    if (completedSurveyNodeIds.size === 0) return config
    const nodes = new Map(config.nodes)
    for (const [key, node] of nodes) {
      const buttons = withoutCompletedSurveyButtons(node.buttons, completedSurveyNodeIds)
      if (buttons.length !== node.buttons.length) nodes.set(key, { ...node, buttons })
    }
    return { ...config, nodes }
  }, [config, completedSurveyNodeIds])

  return (
    <InteractiveVideoPlayerPresentation
      config={displayConfig}
      state={state}
      scenarioId={scenarioId}
      answeredIds={answeredIds}
      videoSurvey={videoSurvey}
      surveyPortalElement={surveyPortalElement}
      pendingEndSurveyCount={pendingEndCount}
      onSurveyAnswered={handleSurveyAnswered}
      onSurveyComplete={handleSurveyComplete}
      onPlayPause={handlePlayPause}
      onButtonSelect={handleButtonSelect}
      onHotspotSelect={handleHotspotSelect}
      onBack={handleBack}
      videoRef={videoRef}
      onTimeUpdate={handleTimeUpdate}
      onVideoEnd={onVideoEnd}
      onToggleMute={handleToggleMute}
      onVolumeChange={handleVolumeChange}
      onAutoplayMuted={handleAutoplayMuted}
      onPlaybackRateChange={handlePlaybackRateChange}
      ProgressBar={ProgressBar}
      ButtonOverlay={ButtonOverlay}
      videoFit={videoFit}
      VideoCanvas={VideoCanvas}
      onClose={onClose}
      isDesktop={isDesktop}
      containerMode={containerMode}
      standaloneAppearance={standaloneAppearance}
      hideBackButton={hideBackButton}
      hideBranchButtons={hideBranchButtons}
      variant={variant}
      wideLayout={wideLayout}
      wideAgendaPlacement={wideAgendaPlacement}
      isAgendaLocked={isSurveyForced}
      isFullscreen={isFullscreen}
      onSeek={handleSeek}
      onToggleFullscreen={handleToggleFullscreen}
    />
  )
}

export default InteractiveVideoPlayer
